import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import Layout from '../components/Layout';
import { useUnion } from '../contexts/UnionContext';
import { useApartment } from '../contexts/ApartmentContext';
import axios from 'axios';
import authService from '../services/authService';

const MONTH_NAMES = [
  '', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function SentInvoices() {
  const { t } = useTranslation();
  usePageTitle(t('sentInvoices.title'));
  const { activeUnion, isUnionAdmin } = useUnion();
  const { activeApartment } = useApartment();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => {
    if (!activeUnion) { setLoading(false); return; }
    setLoading(true);
    const params = activeApartment ? { apartment: activeApartment.id } : {};
    axios.get('/api/sent-invoices/', { headers: authService.authHeader(), params })
      .then(res => {
        setInvoices(Array.isArray(res.data) ? res.data : res.data.results || []);
        setError('');
      })
      .catch(() => setError(t('sentInvoices.failedToLoad')))
      .finally(() => setLoading(false));
  }, [activeUnion, activeApartment, t]);

  const handleTogglePaid = async (inv) => {
    setTogglingId(inv.id);
    try {
      const res = await axios.post(
        `/api/sent-invoices/${inv.id}/mark-paid/`,
        { paid: !inv.paid_at },
        { headers: authService.authHeader() }
      );
      setInvoices(prev => prev.map(i => i.id === inv.id ? res.data : i));
    } catch {
      // silent — could show a snackbar here
    } finally {
      setTogglingId(null);
    }
  };

  if (!activeUnion) {
    return (
      <Layout>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">{t('sentInvoices.selectUnion')}</Alert>
        </Box>
      </Layout>
    );
  }

  const filtered = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      String(inv.apartment_number).toLowerCase().includes(q) ||
      (inv.recipients || []).some(r => r.toLowerCase().includes(q)) ||
      String(inv.period_year).includes(q) ||
      (inv.sent_by_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <Box sx={{ py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4">{t('sentInvoices.title')}</Typography>
          <TextField
            size="small"
            placeholder={t('sentInvoices.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
            sx={{ width: 260 }}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">
              {invoices.length === 0 ? t('sentInvoices.noInvoices') : t('sentInvoices.noResults')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {isUnionAdmin && <TableCell>{t('meterReadings.apartment')}</TableCell>}
                  <TableCell>{t('sentInvoices.period')}</TableCell>
                  <TableCell align="right">{t('sentInvoices.amount')}</TableCell>
                  <TableCell>{t('sentInvoices.recipients')}</TableCell>
                  {isUnionAdmin && <TableCell>{t('sentInvoices.sentBy')}</TableCell>}
                  <TableCell>{t('sentInvoices.sentAt')}</TableCell>
                  <TableCell>{t('sentInvoices.pdf')}</TableCell>
                  <TableCell>{t('dashboard.paid')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(inv => (
                  <TableRow key={inv.id} hover>
                    {isUnionAdmin && (
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {t('meterReadings.apt')} {inv.apartment_number}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      {MONTH_NAMES[inv.period_month]} {inv.period_year}
                    </TableCell>
                    <TableCell align="right">
                      {parseFloat(inv.total_amount).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(inv.recipients || []).map(email => (
                          <Chip key={email} label={email} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    {isUnionAdmin && <TableCell>{inv.sent_by_name || '—'}</TableCell>}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(inv.sent_at).toLocaleString()}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {inv.pdf_url ? (
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title={t('sentInvoices.previewPdf')}>
                            <IconButton size="small" onClick={() => window.open(inv.pdf_url, '_blank')}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('sentInvoices.downloadPdf')}>
                            <IconButton
                              size="small"
                              component="a"
                              href={inv.pdf_url}
                              download={inv.filename || 'invoice.pdf'}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {inv.paid_at ? (
                        <Tooltip title={isUnionAdmin ? t('sentInvoices.paidClickUnmark', { date: new Date(inv.paid_at).toLocaleString() }) : t('sentInvoices.paidAt', { date: new Date(inv.paid_at).toLocaleString() })}>
                          {isUnionAdmin ? (
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleTogglePaid(inv)}
                              disabled={togglingId === inv.id}
                            >
                              {togglingId === inv.id
                                ? <CircularProgress size={16} />
                                : <CheckCircleIcon fontSize="small" />}
                            </IconButton>
                          ) : (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label={new Date(inv.paid_at).toLocaleDateString()}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </Tooltip>
                      ) : (
                        isUnionAdmin ? (
                          <Tooltip title={t('sentInvoices.markAsPaid')}>
                            <IconButton
                              size="small"
                              color="default"
                              onClick={() => handleTogglePaid(inv)}
                              disabled={togglingId === inv.id}
                            >
                              {togglingId === inv.id
                                ? <CircularProgress size={16} />
                                : <RadioButtonUncheckedIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">—</Typography>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Layout>
  );
}

export default SentInvoices;
